import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Star, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const avatars = [
  "https://images.unsplash.com/photo-1664312572933-0563f14484a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwdHJhdmVsZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYyOTE5MTh8MA&ixlib=rb-4.1.0&q=85&w=100",
  "https://images.unsplash.com/photo-1658922184388-acc04afe1e90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwdHJhdmVsZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYyOTE5MTh8MA&ixlib=rb-4.1.0&q=85&w=100",
  "https://images.unsplash.com/photo-1609668232942-1c74ddd6facd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwdHJhdmVsZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYyOTE5MTh8MA&ixlib=rb-4.1.0&q=85&w=100",
];

const countryFlags = {
  "United States": "\uD83C\uDDFA\uD83C\uDDF8",
  "United Kingdom": "\uD83C\uDDEC\uD83C\uDDE7",
  Germany: "\uD83C\uDDE9\uD83C\uDDEA",
  Australia: "\uD83C\uDDE6\uD83C\uDDFA",
  Japan: "\uD83C\uDDEF\uD83C\uDDF5",
  Brazil: "\uD83C\uDDE7\uD83C\uDDF7",
  France: "\uD83C\uDDEB\uD83C\uDDF7",
  Thailand: "\uD83C\uDDF9\uD83C\uDDED",
};

export default function Testimonials({ data }) {
  const { t } = useTranslation();

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1e3a5f]">
            {t("testimonials.title")}
          </h2>
        </motion.div>

        {!data ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="border border-[#e2e8f0] rounded-2xl p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-16 w-full mb-4" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {data.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white border border-[#e2e8f0] rounded-2xl p-6 hover:shadow-md hover:scale-[1.02] transition-all duration-300"
                data-testid={`testimonial-card-${i}`}
              >
                {/* Stars */}
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-[#1f2937] text-sm leading-relaxed mb-5">
                  "{item.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <img
                    src={avatars[i % avatars.length]}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1f2937]">{item.name}</p>
                    <div className="flex items-center gap-2 text-xs text-[#6b7280]">
                      <span>{countryFlags[item.country] || ""} {item.country}</span>
                      <span className="w-1 h-1 rounded-full bg-[#d1d5db]" />
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.destination}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
